import { theme } from 'antd';
import { DisableIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DisablePeopleModal = ({ visible: visibleProp, setVisible: setVisibleProp, children, personIDs = [] }) => {
	// ✅ Hooks
	const canDisablePerson = useManagePermittedRoles('disablePerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: { paddingLG, colorWarning, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Get all people from Redux
	const people = useSelector(selectPeople);

	// ✅ Get loading and success states from Redux
	const { loadingDisableProfile, disablePersonComplete } = useSelector((state) => state.People);

	// ✅ Memoize selected people to avoid recalculations
	const selectedPeople = useMemo(
		() => people.filter((person) => personIDs.includes(person.personID)),
		[people, personIDs]
	);

	// ✅ Memoize names of selected people
	const selectedNames = useMemo(
		() =>
			selectedPeople.map((person) =>
				person.firstName && person.lastName
					? `${person.firstName} ${person.lastName}`
					: person.displayName || person.email || '-'
			),
		[selectedPeople]
	);

	// ✅ Generate dynamic messages
	const getDisableTitle = () =>
		personIDs.length > 1
			? `Are you sure you want to disable these ${personIDs.length} profiles?`
			: 'Are you sure you want to disable this profile?';

	const getDisableButtonText = () => (personIDs.length > 1 ? 'Disable Profiles' : 'Disable');

	// ✅ Close modal function (memoized)
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle Finish (Disable Person(s)) (memoized)
	const handleFinish = useCallback(() => {
		dispatch(peopleActions.disablePerson({ personIDs }));
	}, [dispatch, personIDs]);

	// ✅ Close modal when disable is successful
	useEffect(() => {
		if (disablePersonComplete) handleCancel();
	}, [disablePersonComplete, handleCancel]);

	return (
		<>
			<Tooltip
				title={!canDisablePerson ? permissionDeniedMessage : "Disable this Person's Profile"}
				placement='top'
			>
				<span onClick={(e) => canDisablePerson && setVisible(true)}>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<DisableIcon color='currentColor' width='16' height='16' />}
							disabled={loadingDisableProfile || !canDisablePerson}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingDisableProfile} block>
							Cancel
						</Button>
						<Button
							onClick={handleFinish}
							loading={loadingDisableProfile}
							disabled={loadingDisableProfile}
							type='primary'
							style={{ background: colorWarning }}
							block
						>
							{getDisableButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DisableIcon width={80} height={80} />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{getDisableTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action prevents this person from
								<br /> accessing bookings.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected People</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DisablePeopleModal;
